/**
 * Draft state for the rule surface: one published snapshot, one editable draft, an
 * undo stack, and a diff between them.
 *
 * Mirrors the prototype's `published` / `draft` / `undoStack` triple rather than
 * reaching for a store library, for one reason that is a product requirement and not
 * a preference: **the diff is a feature.** "变更单 CR-xxxx · 改了 N 处 · 历史会话中 M 个
 * 推荐会变" is the headline of the admin surface, so the draft has to be plain,
 * JSON-serialisable, deeply comparable data with stable field order. A reducer over
 * an immutable snapshot gives that for free; a graph of observable objects does not.
 *
 * ## Nothing here persists
 *
 * There is no save. `SalesRecommendationHub` has no API that writes a questionnaire
 * definition — the five `q_1_*.json` files are loaded from the Lambda's classpath in a
 * `static {}` block, so today "change a question" means "edit the JSON, build, deploy".
 * The draft therefore lives in the browser tab and is lost on refresh, exactly like the
 * prototype. That is called out in the UI instead of being hidden, because a surface
 * that looks like it saved and did not is worse than one that says it cannot.
 */
import type { QuestionnaireDefinition, QuestionnaireNode } from '../types';
import type { NewNodeKind } from '../utils/graphEdit';
import {
  addBranch,
  addDetachedNode,
  applyDelete,
  connectNodes,
  disconnectEdge,
  insertOnEdge,
  moveRule,
  setEdgeCondition,
} from '../utils/graphEdit';

/** Hand-placed node coordinates, keyed by `nodeId`. */
export type PositionMap = Record<string, { x: number; y: number }>;

/**
 * One undo step.
 *
 * `positions` is view state and stays out of the record, so a step carries only the
 * positions that step DESTROYED — which in practice means deleting a node, the one
 * action that drops coordinates. Undo has to give those back: dragging a node,
 * deleting it and undoing used to return the node at its derived position, quietly
 * discarding where the user had put it. Snapshotting the whole map instead would make
 * undo reverse drags too, which is not what it is for.
 */
interface UndoEntry {
  draft: QuestionnaireDefinition;
  /** Coordinates this step removed, to be restored if it is undone. */
  prunedPositions?: PositionMap;
}

export interface DraftState {
  /** What the service would load today. Never mutated. */
  published: QuestionnaireDefinition;
  /** The edited copy. */
  draft: QuestionnaireDefinition;
  /** Previous drafts, newest last. Capped — see MAX_UNDO. */
  undoStack: UndoEntry[];
  /**
   * Manual positions, overriding the derived layout for the nodes present.
   *
   * Kept OUTSIDE the definition, because it is not part of the record:
   * `QuestionnaireNode` has no x/y, so there is nowhere to save a dragged position and
   * nothing for the change list to report. Treating it as view state rather than data
   * is what stops the surface from implying otherwise.
   */
  positions: PositionMap;
  /**
   * The node the last action CREATED, if any — `add-node`, `insert-on-edge` and
   * `add-branch` all mint one.
   *
   * Here rather than returned from the graphEdit helpers because the page needs it one
   * render later, to pan to the node and select it: pressing ＋问题 used to append a
   * card at the far corner of the graph without moving the viewport or selecting
   * anything, which read as the button doing nothing. Cleared by every other action so
   * it can never re-trigger a pan.
   */
  lastAddedNodeId?: string;
  /**
   * Which node+fields the last `patch-node` touched, so a run of keystrokes in one
   * field collapses into ONE undo step.
   *
   * The inspector writes straight into the draft on every `onChange` — deliberately,
   * since there is no per-card Save and losing an unfocused edit would be worse. But
   * without coalescing, typing a ten-character question meant ten undo steps and ten
   * presses to get back.
   */
  lastPatchKey?: string;
}

/**
 * Undo depth. Deep-cloning a 15-node definition is a few KB, so the cap is about not
 * growing without bound in a long editing session rather than about memory pressure.
 */
const MAX_UNDO = 50;

/**
 * The node id present in `after` but not `before`.
 *
 * Derived rather than returned from each helper: `addDetachedNode`, `insertOnEdge` and
 * `addBranch` would all need a second return value, and every call site and test with
 * them. Over 30 nodes the diff is free, and it reads in one place.
 */
function addedNodeId(
  before: QuestionnaireDefinition,
  after: QuestionnaireDefinition,
): string | undefined {
  if (after === before) return undefined;
  const had = new Set(before.nodes.map((n) => n.nodeId));
  return after.nodes.find((n) => !had.has(n.nodeId))?.nodeId;
}

/** Push the current draft onto the undo stack, newest last, capped at MAX_UNDO. */
function pushUndo(state: DraftState, prunedPositions?: PositionMap): UndoEntry[] {
  return [...state.undoStack, { draft: clone(state.draft), prunedPositions }].slice(-MAX_UNDO);
}

export type DraftAction =
  | { type: 'reset'; definition: QuestionnaireDefinition }
  /** Replace one node, matched on `nodeId`. The only mutation the inspector needs. */
  | { type: 'patch-node'; nodeId: string; patch: Partial<QuestionnaireNode> }
  /** Delete a question, re-pointing predecessors and dropping stranded nodes. */
  | { type: 'delete-node'; nodeId: string }
  /** Splice a new question onto the `source → target` edge. */
  | { type: 'insert-on-edge'; sourceNodeId: string; targetNodeId: string }
  /** Hang a new question off `fromNodeId`, reached when `expression` is true. */
  | { type: 'add-branch'; fromNodeId: string; expression: string }
  /** Drop an unwired question card on the canvas, to be configured and then connected. */
  /**
   * `at` pins the new card where the user is looking instead of letting the layout
   * park it in the last row — see `ViewportCentre`. It goes into `positions`, the same
   * override a drag writes, so nothing new has to understand it.
   */
  | { type: 'add-node'; at?: { x: number; y: number }; kind?: NewNodeKind }
  /** Wire `source → target`. `expression` is required for any edge after the first. */
  | { type: 'connect'; sourceNodeId: string; targetNodeId: string; expression?: string }
  | { type: 'disconnect'; sourceNodeId: string; targetNodeId: string; ruleIndex?: number }
  /** Re-order one of a node's conditional edges. Order decides which branch can fire. */
  | { type: 'move-rule'; nodeId: string; from: number; to: number }
  /** Set or clear the condition on an edge. `null` makes it the unconditional one. */
  | {
      type: 'set-edge-condition';
      sourceNodeId: string;
      targetNodeId: string;
      expression: string | null;
    }
  | { type: 'set-position'; nodeId: string; x: number; y: number }
  /** Restore a whole saved position map (from localStorage). */
  /**
   * A drop that displaced other cards — one undo step for the whole cascade, because
   * the user made one gesture. See `planDrop`.
   */
  | { type: 'set-positions'; positions: PositionMap }
  | { type: 'load-positions'; positions: PositionMap }
  /** Drop all manual positions and go back to the derived layout. */
  | { type: 'reset-positions' }
  | { type: 'undo' }
  | { type: 'discard' };

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

export function initDraft(definition: QuestionnaireDefinition): DraftState {
  return {
    published: clone(definition),
    draft: clone(definition),
    undoStack: [],
    positions: {},
  };
}

/** The three actions that mint a node. Only these may leave `lastAddedNodeId` set. */
/** Fields touched, so consecutive edits to the SAME field coalesce but a move to a
 *  different field starts a new undo step. */
function patchKey(nodeId: string, patch: object): string {
  return `${nodeId}:${Object.keys(patch).sort().join(',')}`;
}

const CREATES_NODE: ReadonlySet<DraftAction['type']> = new Set([
  'add-node',
  'insert-on-edge',
  'add-branch',
]);

/**
 * `lastAddedNodeId` is a ONE-SHOT signal, not durable state.
 *
 * Every case below spreads `...state`, so without this wrapper the id would survive
 * subsequent edits and the canvas would keep panning back to a node the user created
 * ten actions ago. Clearing it centrally beats remembering to clear it in each of the
 * fifteen other cases.
 */
export function draftReducer(state: DraftState, action: DraftAction): DraftState {
  const next = reduce(state, action);
  if (next === state) return next;
  let out = next;
  if (!CREATES_NODE.has(action.type) && out.lastAddedNodeId !== undefined) {
    const { lastAddedNodeId: _consumed, ...rest } = out;
    out = rest;
  }
  // Same one-shot treatment: leaving a stale patch key set would let an unrelated
  // later edit of the same field silently join the previous undo step.
  if (action.type !== 'patch-node' && out.lastPatchKey !== undefined) {
    const { lastPatchKey: _dropped, ...rest } = out;
    out = rest;
  }
  return out;
}

function reduce(state: DraftState, action: DraftAction): DraftState {
  switch (action.type) {
    case 'reset':
      return initDraft(action.definition);

    case 'patch-node': {
      const idx = state.draft.nodes.findIndex((n) => n.nodeId === action.nodeId);
      if (idx < 0) return state;
      const nodes = [...state.draft.nodes];
      // Undefined values in the patch DELETE the key rather than storing `undefined`:
      // a node carrying `nextNodeId: undefined` would serialise to `"nextNodeId": null`
      // in a future save and the engine reads that as a value, not an absence.
      const merged = { ...nodes[idx] } as unknown as Record<string, unknown>;
      for (const [k, v] of Object.entries(action.patch)) {
        if (v === undefined) delete merged[k];
        else merged[k] = v;
      }
      nodes[idx] = merged as unknown as QuestionnaireNode;
      // One undo step per FIELD, not per keystroke: a run of edits to the same node
      // and the same keys extends the step already on the stack.
      const key = patchKey(action.nodeId, action.patch);
      const coalesce = state.lastPatchKey === key && state.undoStack.length > 0;
      return {
        ...state,
        draft: { ...state.draft, nodes },
        lastPatchKey: key,
        undoStack: coalesce ? state.undoStack : pushUndo(state),
      };
    }

    case 'delete-node': {
      const next = applyDelete(state.draft, action.nodeId);
      // applyDelete returns the SAME object when it refuses, so a blocked delete
      // must not land on the undo stack as a phantom step.
      if (next === state.draft) return state;
      // Positions of nodes that no longer exist are dropped with them.
      const live = new Set(next.nodes.map((n) => n.nodeId));
      const positions = Object.fromEntries(
        Object.entries(state.positions).filter(([id]) => live.has(id)),
      );
      const prunedPositions = Object.fromEntries(
        Object.entries(state.positions).filter(([id]) => !live.has(id)),
      );
      return {
        ...state,
        draft: next,
        positions,
        undoStack: pushUndo(state, prunedPositions),
      };
    }

    case 'insert-on-edge': {
      const next = insertOnEdge(state.draft, action.sourceNodeId, action.targetNodeId);
      if (next === state.draft) return state;
      return {
        ...state,
        draft: next,
        lastAddedNodeId: addedNodeId(state.draft, next),
        undoStack: pushUndo(state),
      };
    }

    case 'add-node': {
      const next = addDetachedNode(state.draft, action.kind);
      const id = addedNodeId(state.draft, next);
      return {
        ...state,
        draft: next,
        positions:
          id && action.at ? { ...state.positions, [id]: action.at } : state.positions,
        lastAddedNodeId: id,
        undoStack: pushUndo(state),
      };
    }

    case 'connect': {
      const next = connectNodes(
        state.draft,
        action.sourceNodeId,
        action.targetNodeId,
        action.expression,
      );
      if (next === state.draft) return state;
      return {
        ...state,
        draft: next,
        undoStack: pushUndo(state),
      };
    }

    case 'disconnect': {
      const next = disconnectEdge(
        state.draft,
        action.sourceNodeId,
        action.targetNodeId,
        action.ruleIndex,
      );
      if (next === state.draft) return state;
      return {
        ...state,
        draft: next,
        undoStack: pushUndo(state),
      };
    }

    case 'set-edge-condition': {
      const next = setEdgeCondition(
        state.draft,
        action.sourceNodeId,
        action.targetNodeId,
        action.expression,
      );
      if (next === state.draft) return state;
      return {
        ...state,
        draft: next,
        undoStack: pushUndo(state),
      };
    }

    case 'move-rule': {
      const next = moveRule(state.draft, action.nodeId, action.from, action.to);
      if (next === state.draft) return state;
      return {
        ...state,
        draft: next,
        undoStack: pushUndo(state),
      };
    }

    case 'add-branch': {
      const next = addBranch(state.draft, action.fromNodeId, action.expression);
      if (next === state.draft) return state;
      return {
        ...state,
        draft: next,
        lastAddedNodeId: addedNodeId(state.draft, next),
        undoStack: pushUndo(state),
      };
    }

    case 'set-position':
      // Not undoable on purpose: dragging is view state, and mixing it into the undo
      // stack would make ⌘Z step through nudges instead of through rule changes.
      return {
        ...state,
        positions: { ...state.positions, [action.nodeId]: { x: action.x, y: action.y } },
      };

    case 'set-positions':
      return { ...state, positions: { ...state.positions, ...action.positions } };

    case 'load-positions':
      // Not undoable, same reasoning as set-position: it is view state.
      return { ...state, positions: action.positions };

    case 'reset-positions':
      return { ...state, positions: {} };

    case 'undo': {
      if (!state.undoStack.length) return state;
      const prev = state.undoStack[state.undoStack.length - 1];
      return {
        ...state,
        draft: prev.draft,
        // Coordinates the undone step destroyed come back with the nodes they belong
        // to. Current positions win on conflict: a node dragged since the delete keeps
        // where it is now.
        positions: prev.prunedPositions
          ? { ...prev.prunedPositions, ...state.positions }
          : state.positions,
        undoStack: state.undoStack.slice(0, -1),
      };
    }

    case 'discard':
      // Positions survive. Cancel reverts the RULE edits; the dragged layout is view
      // state that was never part of what is being discarded (see `positions` above),
      // and because it is persisted on every change, resetting it here silently threw
      // away the user's saved shape. `reset-positions` is the action that clears it.
      return { ...initDraft(state.published), positions: state.positions };

    default:
      return state;
  }
}

/** One line of the change list. `path` is addressable so the UI can jump to it. */
export interface DiffEntry {
  nodeId: string;
  field: string;
  before: string;
  after: string;
}

const show = (v: unknown): string => {
  if (v === undefined) return '—';
  if (v === null) return 'null';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
};

/**
 * Field-level diff of draft against published.
 *
 * Reports per FIELD, not per node, because "改了 2 处" has to mean something a reviewer
 * can check. Node add/remove is reported as a whole-node entry — the surface cannot add
 * or remove nodes yet (no write API, and deletion needs the "which rules stop firing"
 * analysis the prototype has), but the diff handles it so that capability does not need
 * this function rewritten.
 */
export function diffDefinitions(
  published: QuestionnaireDefinition,
  draft: QuestionnaireDefinition,
): DiffEntry[] {
  const out: DiffEntry[] = [];
  const pubById = new Map(published.nodes.map((n) => [n.nodeId, n]));
  const draftById = new Map(draft.nodes.map((n) => [n.nodeId, n]));

  for (const n of draft.nodes) {
    const before = pubById.get(n.nodeId);
    if (!before) {
      out.push({ nodeId: n.nodeId, field: '(整个节点)', before: '—', after: '新增' });
      continue;
    }
    const keys = [...new Set([...Object.keys(before), ...Object.keys(n)])].sort();
    for (const k of keys) {
      const a = (before as unknown as Record<string, unknown>)[k];
      const b = (n as unknown as Record<string, unknown>)[k];
      if (JSON.stringify(a) !== JSON.stringify(b)) {
        out.push({ nodeId: n.nodeId, field: k, before: show(a), after: show(b) });
      }
    }
  }
  for (const n of published.nodes) {
    if (!draftById.has(n.nodeId)) {
      out.push({ nodeId: n.nodeId, field: '(整个节点)', before: '存在', after: '已删除' });
    }
  }
  return out;
}
